import React from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '~/features/chatbot/types/chatbot.types';

interface MessageBubbleProps {
    message: ChatMessage;
}

const markdownStyles: Record<string, React.CSSProperties> = {
    container: {
        fontSize: '14px',
        lineHeight: '1.5',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    codeBlock: {
        display: 'block',
        background: '#1e1e1e',
        color: '#d4d4d4',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '12px',
        overflowX: 'auto',
        margin: '8px 0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    inlineCode: {
        background: '#e8e8e8',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '13px',
    },
};

const MessageBubble = ({ message }: MessageBubbleProps) => {
    const isUser = message.role === 'USER';
    const isRecovery = message.isRecoveryMessage === true;

    if (isRecovery) {
        return (
            <div
                className="display-flex justify-content-start margin-bottom-10"
                data-testid="message-bubble-recovery"
            >
                <div className="alert alert-info max-width-75 margin-bottom-0" role="alert">
                    <span
                        className="rioglyph rioglyph-info-sign margin-right-5"
                        aria-hidden="true"
                    />
                    <span className="text-size-14">
                        {message.content}{' '}
                        <a
                            href="https://slack.com/app_redirect?channel=mdmsupport"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="recovery-slack-link"
                        >
                            #mdmsupport
                        </a>
                    </span>
                </div>
            </div>
        );
    }

    const containerClasses = [
        'display-flex',
        'margin-bottom-10',
        isUser ? 'justify-content-end' : 'justify-content-start',
    ].join(' ');

    const bubbleClasses = [
        'padding-10',
        'padding-left-15',
        'padding-right-15',
        'rounded',
        isUser ? 'max-width-75' : '',
        isUser ? 'bg-highlight' : 'bg-lighter',
        isUser ? 'text-color-white' : 'text-color-darkest',
    ].filter(Boolean).join(' ');

    return (
        <div className={containerClasses} data-testid={`message-bubble-${message.role === 'USER' ? 'user' : 'assistant'}`}>
            <div className={bubbleClasses} style={!isUser ? { overflowWrap: 'break-word', wordBreak: 'break-word' } : undefined}>
                {isUser ? (
                    <span className="text-size-14 white-space-pre-wrap">{message.content}</span>
                ) : (
                    <div style={markdownStyles.container}>
                        <ReactMarkdown
                            components={{
                                code: ({ node, className, children, ...props }) => {
                                    const isBlock = className?.includes('language-') ||
                                        (typeof children === 'string' && children.includes('\n'));
                                    return isBlock ? (
                                        <code style={markdownStyles.codeBlock} {...props}>
                                            {children}
                                        </code>
                                    ) : (
                                        <code style={markdownStyles.inlineCode} {...props}>
                                            {children}
                                        </code>
                                    );
                                },
                                a: ({ node, ...props }) => (
                                    <a {...props} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }} />
                                ),
                            }}
                        >
                            {message.content}
                        </ReactMarkdown>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageBubble;
